import { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { api } from '../api';

// ─── Constantes nuevas tabs ───
const DIAS_OPCIONES = [30, 60, 90, 120, 150];
const MESES_ES = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

const ESTADO_VENC = {
  ok:      { bg: '#1a3a2a', color: '#4caf50', label: 'Al día'      },
  proximo: { bg: '#2a2a1a', color: '#ffc107', label: 'Próximo'     },
  critico: { bg: '#3a2a1a', color: '#ff9800', label: '⚠️ Urgente'  },
  vencido: { bg: '#3a1a1a', color: '#e63946', label: '🔴 Vencido'  },
};

const ESTADO_PAGO = {
  pendiente: { color: '#555',    label: '⏳ Sin cheque' },
  parcial:   { color: '#ffc107', label: '◑ Parcial'    },
  pagado:    { color: '#4caf50', label: '✅ Pagado'     },
};

const TooltipFlujo = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  const fmt = (n) => n > 0 ? `$${(n||0).toLocaleString('es-EC', {minimumFractionDigits:2})}` : '—';
  return (
    <div style={{ background: '#1a1a2e', border: '1px solid #2d2d44', borderRadius: 8, padding: '10px 14px', fontSize: 12, minWidth: 220 }}>
      <div style={{ color: '#fff', fontWeight: 'bold', marginBottom: 8 }}>{d?.label}</div>
      <div style={{ borderBottom: '1px solid #2d2d44', marginBottom: 6, paddingBottom: 6 }}>
        <div style={{ color: '#4caf50' }}>Ventas neto:      {fmt(d?.ventas_neto)}</div>
        <div style={{ color: '#4caf50', fontSize: 11, paddingLeft: 8 }}>↳ Contado: {fmt(d?.ventas_contado)}</div>
        <div style={{ color: '#7209b7', fontSize: 11, paddingLeft: 8 }}>↳ Crédito: {fmt(d?.ventas_credito)}</div>
        {d?.devoluciones > 0 && <div style={{ color: '#e63946', fontSize: 11, paddingLeft: 8 }}>↳ Devol.: -{fmt(d?.devoluciones)}</div>}
      </div>
      <div style={{ borderBottom: '1px solid #2d2d44', marginBottom: 6, paddingBottom: 6 }}>
        <div style={{ color: '#4cc9f0' }}>Compras:          {fmt(d?.compras_total)}</div>
        <div style={{ color: '#f72585' }}>Cheques emitidos: {fmt(d?.cheques_emitidos)}{d?.num_cheques > 0 ? ` (${d?.num_cheques})` : ''}</div>
        {d?.saldo_pendiente > 0 && <div style={{ color: '#ff9800', fontSize: 11, paddingLeft: 8 }}>Saldo pendiente: {fmt(d?.saldo_pendiente)}</div>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ color: d?.flujo_caja >= 0 ? '#4caf50' : '#e63946', fontWeight: 'bold' }}>
          💵 Flujo caja: {d?.flujo_caja >= 0 ? '+' : ''}{fmt(Math.abs(d?.flujo_caja))}
          <span style={{ fontSize: 10, fontWeight: 'normal', marginLeft: 4 }}>(contado - cheques)</span>
        </div>
        <div style={{ color: d?.resultado_operativo >= 0 ? '#4caf50' : '#e63946', fontWeight: 'bold' }}>
          📊 Resultado: {d?.resultado_operativo >= 0 ? '+' : ''}{fmt(Math.abs(d?.resultado_operativo))}
          <span style={{ fontSize: 10, fontWeight: 'normal', marginLeft: 4 }}>(ventas - compras)</span>
        </div>
      </div>
    </div>
  );
};

export default function Proveedores() {
  // ── Estados existentes ──
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
  const [facturas, setFacturas] = useState([]);
  const [cargandoFacturas, setCargandoFacturas] = useState(false);
  const [facturaDetalle, setFacturaDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [modalFactura, setModalFactura] = useState(false);
  const [busquedaFactura, setBusquedaFactura] = useState('');

  // ── Estados nuevas tabs ──
  const [tab, setTab]                       = useState('compras');
  const [cuentas, setCuentas]               = useState(null);
  const [flujo, setFlujo]                   = useState(null);
  const [condiciones, setCondiciones]       = useState([]);
  const [cargandoNuevo, setCargandoNuevo]   = useState(false);
  const [filtroEstado, setFiltroEstado]     = useState('todos');
  const [modalCond, setModalCond]           = useState(null);
  const [guardandoCond, setGuardandoCond]   = useState(false);
  const [drawerCheques, setDrawerCheques]   = useState(null);
  const [chequesMes, setChequesMes]         = useState(null);

  useEffect(() => { cargar(); }, []);

  // Cargar datos de nuevas tabs cuando se activan
  useEffect(() => {
    if (tab === 'cuentas' && !cuentas)     cargarCuentas();
    if (tab === 'flujo'   && !flujo)       cargarFlujo();
    if (tab === 'condiciones' && condiciones.length === 0) cargarCondiciones();
    if (tab === 'cheques' && !chequesMes)  cargarChequesMes();
  }, [tab]);

  const cargar = async () => {
    setCargando(true);
    try {
      const data = await api.get('/proveedores');
      setDatos(data.error ? null : data);
    } catch (e) { console.error(e); }
    finally { setCargando(false); }
  };

  const cargarCuentas = async () => {
    setCargandoNuevo(true);
    try {
      const data = await api.get('/proveedores/cuentas');
      setCuentas(data.error ? null : data);
    } catch (e) { console.error(e); }
    finally { setCargandoNuevo(false); }
  };

  const cargarFlujo = async () => {
    setCargandoNuevo(true);
    try {
      const data = await api.get('/proveedores/flujo');
      setFlujo(data.error ? null : data);
    } catch (e) { console.error(e); }
    finally { setCargandoNuevo(false); }
  };

  const cargarCondiciones = async () => {
    try {
      const data = await api.get('/proveedores/condiciones');
      setCondiciones(data.condiciones || []);
    } catch (e) { console.error(e); }
  };

  const abrirModalCond = (c = null) => {
    setModalCond(c ? { ...c } : {
      ruc_proveedor: '', nombre_proveedor: '',
      dias_credito: 60, tipo_agrupacion: 'mensual', notas: '',
    });
  };

  const guardarCond = async () => {
    if (!modalCond.ruc_proveedor.trim()) { alert('Ingresa el RUC'); return; }
    setGuardandoCond(true);
    try {
      await api.post('/proveedores/condiciones', modalCond);
      setModalCond(null);
      cargarCondiciones();
      setCuentas(null); // recalcular vencimientos
    } catch (e) { console.error(e); }
    finally { setGuardandoCond(false); }
  };

  const cargarChequesMes = async () => {
    setCargandoNuevo(true);
    try {
      const data = await api.get('/proveedores/cheques_mes');
      setChequesMes(data.error ? null : data);
    } catch (e) { console.error(e); }
    finally { setCargandoNuevo(false); }
  };

  const recargarTodo = () => {
    cargar();
    setCuentas(null);    cargarCuentas();
    setFlujo(null);      cargarFlujo();
    setChequesMes(null); cargarChequesMes();
    cargarCondiciones();
  };

  const seleccionarProveedor = async (proveedor) => {
    setProveedorSeleccionado(proveedor);
    setBusquedaFactura('');
    setFacturas([]);
    setFacturaDetalle(null);
    setCargandoFacturas(true);
    try {
      const data = await api.get(`/proveedores/${proveedor.cedruc}/facturas`);
      setFacturas(data.facturas || []);
    } catch (e) { console.error(e); }
    finally { setCargandoFacturas(false); }
  };

  const verFactura = async (numeroFactura) => {
    setModalFactura(true);
    setCargandoDetalle(true);
    setFacturaDetalle(null);
    try {
      const data = await api.get(`/proveedores/factura?numero=${encodeURIComponent(numeroFactura)}`);
      setFacturaDetalle(data);
    } catch (e) { console.error(e); }
    finally { setCargandoDetalle(false); }
  };

  const cerrarModal = () => { setModalFactura(false); setFacturaDetalle(null); };
  const volver = () => { setProveedorSeleccionado(null); setFacturas([]); };

  const anio = new Date().getFullYear();
  const facturasFiltradas = facturas.filter(f =>
    f.factura.toLowerCase().includes(busquedaFactura.toLowerCase()) ||
    f.fecha.includes(busquedaFactura)
  );

  if (cargando && tab === 'compras') return <p style={s.cargando}>Cargando proveedores...</p>;

  return (
    <div style={{ position: 'relative' }}>
      {/* Header */}
      <div style={s.headerRow}>
        <div>
          <h2 style={s.titulo}>🏭 Proveedores</h2>
          <p style={s.subtitulo}>Compras año {anio} · control de pagos · flujo de caja</p>
        </div>
        <button style={s.botonRefresh} onClick={recargarTodo}>🔄 Actualizar</button>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {[
          { key: 'compras',     label: '📊 Compras'          },
          { key: 'cuentas',     label: '📋 Cuentas por Pagar' },
          { key: 'flujo',       label: '💰 Flujo de Caja'     },
          { key: 'cheques',     label: '🧾 Cheques'            },
          { key: 'condiciones', label: '⚙️ Condiciones'        },
        ].map(t => (
          <button key={t.key}
            style={{ ...s.tab, ...(tab === t.key ? s.tabActivo : {}) }}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ TAB COMPRAS (contenido original) ══ */}
      {tab === 'compras' && (
        <div>
        {!datos ? <p style={{ color: '#e63946' }}>No se pudo cargar</p> :
        !proveedorSeleccionado ? (
          <>
          {/* KPIs */}
          <div style={s.kpisRow}>
            <div style={s.kpiCard}>
              <div style={s.kpiValor}>{datos.total_proveedores}</div>
              <div style={s.kpiLabel}>Proveedores activos</div>
            </div>
            <div style={{...s.kpiCard, borderTopColor: '#4cc9f0'}}>
              <div style={{...s.kpiValor, fontSize: 20}}>${datos.total_gasto.toLocaleString('es-EC', {minimumFractionDigits: 2})}</div>
              <div style={s.kpiLabel}>Gasto neto {anio}</div>
            </div>
            <div style={{...s.kpiCard, borderTopColor: '#4caf50'}}>
              <div style={s.kpiValor}>{datos.proveedores.reduce((a, p) => a + p.total_facturas, 0)}</div>
              <div style={s.kpiLabel}>Facturas emitidas</div>
            </div>
            <div style={{...s.kpiCard, borderTopColor: '#e63946'}}>
              <div style={{...s.kpiValor, color: '#e63946', fontSize: 20}}>
                ${datos.proveedores.reduce((a, p) => a + p.total_devoluciones, 0).toFixed(2)}
              </div>
              <div style={s.kpiLabel}>Total devoluciones</div>
            </div>
          </div>

          {/* Tabla proveedores */}
          <div style={s.tabla}>
            <div style={s.tablaHeader}>
              <span style={{ flex: 3 }}>PROVEEDOR</span>
              <span style={{ flex: 1 }}>RUC</span>
              <span style={{ width: 80, textAlign: 'center' }}>FACTURAS</span>
              <span style={{ width: 120, textAlign: 'right' }}>MONTO BRUTO</span>
              <span style={{ width: 100, textAlign: 'right' }}>DEVOL.</span>
              <span style={{ width: 120, textAlign: 'right' }}>MONTO NETO</span>
              <span style={{ width: 80, textAlign: 'right' }}>% GASTO</span>
              <span style={{ width: 100, textAlign: 'center' }}>PAGO</span>
              <span style={{ width: 110, textAlign: 'right' }}>ÚLT. COMPRA</span>
            </div>
            {datos.proveedores.map((p, i) => (
              <div key={i}
                style={{...s.tablaRow, background: i % 2 === 0 ? '#1e1e2e' : '#1a1a2e', cursor: 'pointer'}}
                onClick={() => seleccionarProveedor(p)}
                onMouseEnter={e => e.currentTarget.style.background = '#2d2d44'}
                onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#1e1e2e' : '#1a1a2e'}
              >
                <span style={{ flex: 3 }}>
                  <div style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>{p.proveedor}</div>
                </span>
                <span style={{ flex: 1, color: '#555', fontSize: 11 }}>{p.cedruc}</span>
                <span style={{ width: 80, textAlign: 'center', color: '#4cc9f0', fontWeight: 'bold' }}>{p.total_facturas}</span>
                <span style={{ width: 120, textAlign: 'right', color: '#aaa' }}>${p.monto_bruto.toFixed(2)}</span>
                <span style={{ width: 100, textAlign: 'right', color: p.total_devoluciones > 0 ? '#e63946' : '#333' }}>
                  {p.total_devoluciones > 0 ? `-$${p.total_devoluciones.toFixed(2)}` : '—'}
                </span>
                <span style={{ width: 120, textAlign: 'right', color: '#4cc9f0', fontWeight: 'bold' }}>${p.monto_neto.toFixed(2)}</span>
                <span style={{ width: 80, textAlign: 'right' }}>
                  <span style={{ background: '#1a2a3e', color: '#4cc9f0', borderRadius: 6, padding: '3px 8px', fontSize: 11 }}>
                    {p.pct_gasto}%
                  </span>
                </span>
                <span style={{ width: 100, textAlign: 'center' }}>
                  <span style={{
                    background: p.monto_credito > p.monto_contado ? '#1a2a3e' : '#1a3a2a',
                    color: p.monto_credito > p.monto_contado ? '#4cc9f0' : '#4caf50',
                    borderRadius: 6, padding: '3px 8px', fontSize: 11
                  }}>
                    {p.monto_credito > p.monto_contado ? 'Crédito' : 'Contado'}
                  </span>
                </span>
                <span style={{ width: 110, textAlign: 'right', color: '#aaa', fontSize: 12 }}>{p.ultima_compra}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Header proveedor */}
          <div style={s.proveedorHeader}>
            <div>
              <h3 style={{ color: '#fff', fontSize: 17, marginBottom: 4 }}>🏭 {proveedorSeleccionado.proveedor}</h3>
              <span style={{ color: '#555', fontSize: 13 }}>RUC: {proveedorSeleccionado.cedruc}</span>
              <span style={{ color: '#555', fontSize: 13, marginLeft: 16 }}>
                {proveedorSeleccionado.total_facturas} facturas · Neto:
                <span style={{ color: '#4cc9f0', fontWeight: 'bold', marginLeft: 6 }}>
                  ${proveedorSeleccionado.monto_neto.toFixed(2)}
                </span>
              </span>
            </div>
            <button style={s.botonVolver} onClick={volver}>← Volver</button>
          </div>

          {/* Facturas */}
          {cargandoFacturas ? (
            <p style={s.cargando}>Cargando facturas...</p>
          ) : facturas.length === 0 ? (
            <p style={{ color: '#555', textAlign: 'center', marginTop: 40 }}>Sin facturas este año</p>
          ) : (
            <>
              <input
                style={{...s.inputBusqueda, marginBottom: 16}}
                value={busquedaFactura}
                onChange={e => setBusquedaFactura(e.target.value)}
                placeholder="Buscar por número de factura o fecha (ej: 001001-000830141 o 15/03/2026)..."
              />
              <div style={s.tabla}>
                <div style={s.tablaHeader}>
                  <span style={{ flex: 2 }}>FACTURA</span>
                  <span style={{ flex: 1 }}>FECHA</span>
                  <span style={{ width: 80, textAlign: 'center' }}>ITEMS</span>
                  <span style={{ width: 120, textAlign: 'right' }}>TOTAL</span>
                  <span style={{ width: 100, textAlign: 'center' }}>FORMA PAGO</span>
                  <span style={{ width: 80, textAlign: 'center' }}>DETALLE</span>
                </div>
                {facturasFiltradas.map((f, i) => (
                  <div key={i} style={{...s.tablaRow, background: i % 2 === 0 ? '#1e1e2e' : '#1a1a2e'}}>
                    <span style={{ flex: 2, color: '#4cc9f0', fontSize: 13, fontFamily: 'monospace' }}>{f.factura}</span>
                    <span style={{ flex: 1, color: '#aaa', fontSize: 13 }}>{f.fecha}</span>
                    <span style={{ width: 80, textAlign: 'center', color: '#fff' }}>{f.total_items}</span>
                    <span style={{ width: 120, textAlign: 'right', color: '#fff', fontWeight: 'bold' }}>${f.total_factura.toFixed(2)}</span>
                    <span style={{ width: 100, textAlign: 'center' }}>
                      <span style={{
                        background: f.forma_pago === 'Crédito' ? '#1a2a3e' : '#1a3a2a',
                        color: f.forma_pago === 'Crédito' ? '#4cc9f0' : '#4caf50',
                        borderRadius: 6, padding: '3px 8px', fontSize: 11
                      }}>{f.forma_pago}</span>
                    </span>
                    <span style={{ width: 80, textAlign: 'center' }}>
                      <button style={s.botonVer} onClick={() => verFactura(f.factura)}>Ver →</button>
                    </span>
                  </div>
                ))}
                <div style={s.totalRow}>
                  <span style={{ color: '#aaa', fontSize: 13 }}>
                    {busquedaFactura ? `${facturasFiltradas.length} resultados` : `Total ${anio}`}:
                  </span>
                  <span style={{ color: '#4cc9f0', fontWeight: 'bold', fontSize: 16, marginLeft: 12 }}>
                    ${facturasFiltradas.reduce((a, f) => a + f.total_factura, 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Modal detalle factura */}
      {modalFactura && (
        <div style={s.modalOverlay} onClick={cerrarModal}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              {facturaDetalle && (
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#fff', fontSize: 15, fontWeight: 'bold', marginBottom: 4 }}>
                    {facturaDetalle.encabezado.proveedor}
                  </div>
                  <div style={{ color: '#555', fontSize: 12 }}>
                    Factura: <span style={{ color: '#4cc9f0' }}>{facturaDetalle.encabezado.factura}</span>
                    <span style={{ marginLeft: 16 }}>Fecha: {facturaDetalle.encabezado.fecha}</span>
                    <span style={{ marginLeft: 16 }}>RUC: {facturaDetalle.encabezado.cedruc}</span>
                    <span style={{ marginLeft: 16 }}>Pago: {facturaDetalle.encabezado.forma_pago}</span>
                  </div>
                </div>
              )}
              <button style={s.modalCerrar} onClick={cerrarModal}>✕</button>
            </div>
            <div style={s.modalBody}>
              {cargandoDetalle ? (
                <p style={s.cargando}>Cargando detalle...</p>
              ) : facturaDetalle ? (
                <>
                  <div style={s.tabla}>
                    <div style={s.tablaHeader}>
                      <span style={{ flex: 1 }}>CÓDIGO</span>
                      <span style={{ flex: 4 }}>DESCRIPCIÓN</span>
                      <span style={{ width: 70, textAlign: 'right' }}>CANT.</span>
                      <span style={{ width: 100, textAlign: 'right' }}>COSTO S/IVA</span>
                      <span style={{ width: 60, textAlign: 'right' }}>IVA%</span>
                      <span style={{ width: 100, textAlign: 'right' }}>COSTO C/IVA</span>
                      <span style={{ width: 90, textAlign: 'right' }}>PVP</span>
                      <span style={{ width: 100, textAlign: 'right' }}>TOTAL</span>
                    </div>
                    {facturaDetalle.items.map((item, i) => (
                      <div key={i} style={{...s.tablaRow, background: i % 2 === 0 ? '#1e1e2e' : '#1a1a2e'}}>
                        <span style={{ flex: 1, color: '#555', fontSize: 11 }}>{item.codigo_bar}</span>
                        <span style={{ flex: 4, color: '#fff', fontSize: 13 }}>{item.descripcion}</span>
                        <span style={{ width: 70, textAlign: 'right', color: '#fff' }}>{item.cantidad.toFixed(0)}</span>
                        <span style={{ width: 100, textAlign: 'right', color: '#aaa' }}>${item.costo_sin_iva.toFixed(2)}</span>
                        <span style={{ width: 60, textAlign: 'right', color: '#555' }}>{item.iva_pct}%</span>
                        <span style={{ width: 100, textAlign: 'right', color: '#aaa' }}>${item.costo_con_iva.toFixed(2)}</span>
                        <span style={{ width: 90, textAlign: 'right', color: '#4caf50' }}>${item.pvp.toFixed(2)}</span>
                        <span style={{ width: 100, textAlign: 'right', color: '#4cc9f0', fontWeight: 'bold' }}>${item.total.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div style={s.totalRow}>
                    <span style={{ color: '#aaa', fontSize: 13 }}>Total factura:</span>
                    <span style={{ color: '#4cc9f0', fontWeight: 'bold', fontSize: 18, marginLeft: 12 }}>
                      ${facturaDetalle.encabezado.total_factura.toFixed(2)}
                    </span>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
      </div>
      )} {/* fin tab compras */}

      {/* ══ TAB CUENTAS POR PAGAR ══ */}
      {tab === 'cuentas' && (
        <div style={{ padding: '24px 0' }}>
          {cargandoNuevo ? <p style={s.cargando}>Cargando cuentas...</p> :
           !cuentas ? <p style={{ color: '#e63946' }}>No se pudo cargar</p> : (
            <>
              {/* KPIs */}
              <div style={s.kpisRow}>
                <div style={{ ...s.kpiCard, borderTopColor: '#e63946' }}>
                  <div style={{ ...s.kpiValor, color: '#e63946', fontSize: 22 }}>
                    ${cuentas.total_por_pagar.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                  </div>
                  <div style={s.kpiLabel}>Total por pagar</div>
                </div>
                <div style={{ ...s.kpiCard, borderTopColor: '#ff9800' }}>
                  <div style={{ ...s.kpiValor, color: '#ff9800', fontSize: 22 }}>
                    ${cuentas.total_vencido.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                  </div>
                  <div style={s.kpiLabel}>🔴 Vencido</div>
                </div>
                <div style={{ ...s.kpiCard, borderTopColor: '#7209b7' }}>
                  <div style={s.kpiValor}>{cuentas.total_cuentas}</div>
                  <div style={s.kpiLabel}>Cuentas activas</div>
                </div>
              </div>

              {/* Filtros */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {[['todos','Todas'],['pendiente','⏳ Sin cheque'],['vencido','🔴 Vencidas'],['pagado','✅ Pagadas']].map(([k,l]) => (
                  <button key={k}
                    style={{ ...s.filtroBtn, ...(filtroEstado === k ? s.filtroBtnActivo : {}) }}
                    onClick={() => setFiltroEstado(k)}>{l}
                  </button>
                ))}
              </div>

              {/* Tabla */}
              <div style={s.tabla}>
                <div style={s.tablaHeader}>
                  <span style={{ flex: 3 }}>PROVEEDOR</span>
                  <span style={{ width: 80,  textAlign: 'center' }}>MES</span>
                  <span style={{ width: 55,  textAlign: 'center' }}>FACTS.</span>
                  <span style={{ width: 120, textAlign: 'right'  }}>SALDO REAL</span>
                  <span style={{ width: 90,  textAlign: 'center' }}>FIN MES</span>
                  <span style={{ flex: 3,    textAlign: 'left'   }}>CUOTAS (vencimiento)</span>
                  <span style={{ width: 110, textAlign: 'center' }}>CHEQUES</span>
                </div>
                {(cuentas.cuentas || [])
                  .filter(c => {
                    if (filtroEstado === 'todos') return true;
                    if (filtroEstado === 'vencido') return c.estado_venc === 'vencido';
                    if (filtroEstado === 'pendiente') return c.estado_venc !== 'vencido';
                    return true;
                  })
                  .map((c, i) => {
                    const ev = ESTADO_VENC[c.estado_venc] || ESTADO_VENC.ok;
                    const [anioM, mesM] = (c.mes || '2026-01').split('-');
                    const mesLabel = `${MESES_ES[parseInt(mesM)]} ${anioM}`;
                    return (
                      <div key={`${c.ruc}_${c.mes}`} style={{
                        ...s.tablaRow,
                        background: i%2===0?'#1e1e2e':'#1a1a2e',
                        borderLeft: `3px solid ${ev.color}`,
                        alignItems: 'flex-start',
                        paddingTop: 14, paddingBottom: 14,
                      }}>
                        {/* Proveedor */}
                        <span style={{ flex: 3 }}>
                          <div style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>{c.nombre}</div>
                          <div style={{ color: '#555', fontSize: 11 }}>{c.ruc}</div>
                        </span>

                        {/* Mes */}
                        <span style={{ width: 80, textAlign: 'center', color: '#aaa', fontSize: 12, paddingTop: 2 }}>{mesLabel}</span>

                        {/* Facturas */}
                        <span style={{ width: 55, textAlign: 'center', color: '#aaa', paddingTop: 2 }}>{c.num_facturas}</span>

                        {/* Saldo real */}
                        <span style={{ width: 120, textAlign: 'right', paddingTop: 2 }}>
                          <div style={{ color: '#e63946', fontWeight: 'bold', fontSize: 14 }}>
                            ${c.saldo.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                          </div>
                          <div style={{ color: '#555', fontSize: 10 }}>
                            total: ${c.monto_total.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                          </div>
                        </span>

                        {/* Fin mes */}
                        <span style={{ width: 90, textAlign: 'center', color: '#aaa', fontSize: 12, paddingTop: 2 }}>{c.fin_mes}</span>

                        {/* Cuotas */}
                        <span style={{ flex: 3 }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {(c.cuotas || []).map((q, qi) => {
                              const ev2 = ESTADO_VENC[q.estado_venc] || ESTADO_VENC.ok;
                              return (
                                <div key={qi} style={{
                                  background: ev2.bg,
                                  border: `1px solid ${ev2.color}`,
                                  borderRadius: 6, padding: '3px 8px',
                                  fontSize: 11, textAlign: 'center',
                                }}>
                                  <div style={{ color: ev2.color, fontWeight: 'bold' }}>
                                    {qi+1}ª ${q.monto.toFixed(2)}
                                  </div>
                                  <div style={{ color: ev2.color, opacity: 0.8, fontSize: 10 }}>
                                    {q.fecha_venc}
                                  </div>
                                  <div style={{ color: '#555', fontSize: 9 }}>
                                    {q.dias_restantes >= 0 ? `en ${q.dias_restantes}d` : `+${Math.abs(q.dias_restantes)}d`}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </span>

                        {/* Cheques reales — abre drawer */}
                        <span style={{ width: 110, textAlign: 'center' }}>
                          {(c.cheques || []).length > 0 ? (
                            <button
                              style={{ background: '#1a3a2a', border: '1px solid #4caf50', borderRadius: 6, color: '#4caf50', padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}
                              onClick={e => { e.stopPropagation(); setDrawerCheques({ nombre: c.nombre, ruc: c.ruc, cheques: c.cheques, recibos: c.recibos || {} }); }}>
                              🧾 {c.cheques.length} cheque(s)
                            </button>
                          ) : (
                            <span style={{ color: '#333', fontSize: 11 }}>Sin cheques</span>
                          )}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ══ TAB FLUJO DE CAJA ══ */}
      {tab === 'flujo' && (
        <div style={{ padding: '24px 0' }}>
          {cargandoNuevo ? <p style={s.cargando}>Cargando flujo...</p> :
           !flujo ? <p style={{ color: '#e63946' }}>No se pudo cargar</p> : (
            <>
              {/* KPIs mes actual */}
              {(() => {
                const m = flujo.meses.find(x => x.es_actual);
                if (!m) return null;
                return (
                  <div style={s.kpisRow}>
                    <div style={{ ...s.kpiCard, borderTopColor: '#4caf50' }}>
                      <div style={{ ...s.kpiValor, color: '#4caf50', fontSize: 20 }}>${m.ventas_neto.toLocaleString('es-EC', { minimumFractionDigits: 2 })}</div>
                      <div style={s.kpiLabel}>Ventas neto {m.label}</div>
                      <div style={{ color: '#4caf50', fontSize: 11, marginTop: 4 }}>Contado: ${m.ventas_contado.toLocaleString('es-EC', { minimumFractionDigits: 2 })}</div>
                    </div>
                    <div style={{ ...s.kpiCard, borderTopColor: '#4cc9f0' }}>
                      <div style={{ ...s.kpiValor, color: '#4cc9f0', fontSize: 20 }}>${m.compras_total.toLocaleString('es-EC', { minimumFractionDigits: 2 })}</div>
                      <div style={s.kpiLabel}>Compras {m.label}</div>
                      {m.saldo_pendiente > 0 && <div style={{ color: '#ff9800', fontSize: 11, marginTop: 4 }}>Pendiente: ${m.saldo_pendiente.toLocaleString('es-EC', { minimumFractionDigits: 2 })}</div>}
                    </div>
                    <div style={{ ...s.kpiCard, borderTopColor: '#f72585' }}>
                      <div style={{ ...s.kpiValor, color: '#f72585', fontSize: 20 }}>${m.cheques_emitidos.toLocaleString('es-EC', { minimumFractionDigits: 2 })}</div>
                      <div style={s.kpiLabel}>Cheques emitidos</div>
                      <div style={{ color: '#555', fontSize: 11, marginTop: 4 }}>{m.num_cheques} cheques</div>
                    </div>
                    <div style={{ ...s.kpiCard, borderTopColor: m.flujo_caja >= 0 ? '#4caf50' : '#e63946' }}>
                      <div style={{ ...s.kpiValor, color: m.flujo_caja >= 0 ? '#4caf50' : '#e63946', fontSize: 20 }}>
                        {m.flujo_caja >= 0 ? '+' : ''}${m.flujo_caja.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                      </div>
                      <div style={s.kpiLabel}>💵 Flujo de caja</div>
                      <div style={{ color: '#555', fontSize: 10, marginTop: 4 }}>Contado - Cheques emitidos</div>
                    </div>
                    <div style={{ ...s.kpiCard, borderTopColor: m.resultado_operativo >= 0 ? '#4caf50' : '#e63946' }}>
                      <div style={{ ...s.kpiValor, color: m.resultado_operativo >= 0 ? '#4caf50' : '#e63946', fontSize: 20 }}>
                        {m.resultado_operativo >= 0 ? '+' : ''}${m.resultado_operativo.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                      </div>
                      <div style={s.kpiLabel}>📊 Resultado operativo</div>
                      <div style={{ color: '#555', fontSize: 10, marginTop: 4 }}>Ventas neto - Compras</div>
                    </div>
                  </div>
                );
              })()}

              {/* Gráfico */}
              <div style={{ background: '#1e1e2e', borderRadius: 12, padding: 20, marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <span style={{ color: '#fff', fontWeight: 'bold', fontSize: 15 }}>📊 Flujo de Caja — 6 meses atrás / 3 meses adelante</span>
                  <span style={{ color: '#555', fontSize: 11 }}>Barras transparentes = proyectado</span>
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={flujo.meses} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" vertical={false} />
                    <XAxis dataKey="label_corto" stroke="#555" fontSize={11} tick={{ fill: '#888' }} />
                    <YAxis stroke="#555" fontSize={11} tick={{ fill: '#888' }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<TooltipFlujo />} />
                    <Legend wrapperStyle={{ color: '#aaa', fontSize: 12 }} />
                    <Bar dataKey="ventas_contado"  name="Ventas Contado"   stackId="ventas" fill="#4caf50" fillOpacity={0.85} radius={[0,0,0,0]} />
                    <Bar dataKey="ventas_credito"  name="Ventas Crédito"   stackId="ventas" fill="#7209b7" fillOpacity={0.85} radius={[3,3,0,0]} />
                    <Bar dataKey="compras_total"   name="Compras"          fill="#4cc9f0"  fillOpacity={0.7}  radius={[3,3,0,0]} />
                    <Bar dataKey="cheques_emitidos" name="Cheques emitidos" fill="#f72585"  fillOpacity={0.85} radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Tabla mensual */}
              <div style={{ background: '#1e1e2e', borderRadius: 12, padding: 20 }}>
                <div style={{ color: '#fff', fontWeight: 'bold', fontSize: 15, marginBottom: 16 }}>📋 Detalle mensual</div>
                <div style={s.tabla}>
                  <div style={s.tablaHeader}>
                    <span style={{ flex: 2 }}>MES</span>
                    <span style={{ width: 120, textAlign: 'right' }}>VENTAS NETO</span>
                    <span style={{ width: 110, textAlign: 'right' }}>CONTADO</span>
                    <span style={{ width: 110, textAlign: 'right' }}>COMPRAS</span>
                    <span style={{ width: 110, textAlign: 'right' }}>CHEQUES EMIT.</span>
                    <span style={{ width: 120, textAlign: 'right' }}>💵 FLUJO CAJA</span>
                    <span style={{ width: 120, textAlign: 'right' }}>📊 RESULTADO</span>
                  </div>
                  {flujo.meses.map((m, i) => (
                    <div key={i} style={{
                      ...s.tablaRow,
                      background: m.es_actual ? '#1a2a3e' : i%2===0?'#1e1e2e':'#1a1a2e',
                      borderLeft: m.es_actual ? '3px solid #4cc9f0' : '3px solid transparent',
                      opacity: m.es_futuro ? 0.7 : 1,
                    }}>
                      <span style={{ flex: 2, color: m.es_actual ? '#4cc9f0' : '#fff', fontWeight: m.es_actual ? 'bold' : 'normal', fontSize: 13 }}>
                        {m.label}
                        {m.es_actual && <span style={{ color: '#4cc9f0', fontSize: 10, marginLeft: 8 }}>● Actual</span>}
                        {m.es_futuro && <span style={{ color: '#555', fontSize: 10, marginLeft: 8 }}>proyectado</span>}
                      </span>
                      <span style={{ width: 120, textAlign: 'right', color: '#4caf50', fontWeight: 'bold' }}>
                        {m.ventas_neto > 0 ? `$${m.ventas_neto.toLocaleString('es-EC', { minimumFractionDigits: 2 })}` : '—'}
                      </span>
                      <span style={{ width: 110, textAlign: 'right', color: '#4caf50', fontSize: 12 }}>
                        {m.ventas_contado > 0 ? `$${m.ventas_contado.toLocaleString('es-EC', {minimumFractionDigits:2})}` : '—'}
                      </span>
                      <span style={{ width: 110, textAlign: 'right', color: '#4cc9f0', fontSize: 12 }}>
                        {m.compras_total > 0 ? `$${m.compras_total.toLocaleString('es-EC', {minimumFractionDigits:2})}` : '—'}
                      </span>
                      <span style={{ width: 110, textAlign: 'right', color: '#f72585', fontSize: 12 }}>
                        {m.cheques_emitidos > 0 ? `$${m.cheques_emitidos.toLocaleString('es-EC', {minimumFractionDigits:2})}` : '—'}
                        {m.num_cheques > 0 && <span style={{color:'#555',fontSize:10,marginLeft:4}}>({m.num_cheques})</span>}
                      </span>
                      <span style={{ width: 120, textAlign: 'right', fontWeight: 'bold',
                        color: m.flujo_caja >= 0 ? '#4caf50' : '#e63946' }}>
                        {(m.ventas_contado > 0 || m.cheques_emitidos > 0)
                          ? `${m.flujo_caja >= 0 ? '+' : ''}$${m.flujo_caja.toLocaleString('es-EC', {minimumFractionDigits:2})}`
                          : '—'}
                      </span>
                      <span style={{ width: 120, textAlign: 'right', fontWeight: 'bold',
                        color: m.resultado_operativo >= 0 ? '#4caf50' : '#e63946' }}>
                        {(m.ventas_neto > 0 || m.compras_total > 0)
                          ? `${m.resultado_operativo >= 0 ? '+' : ''}$${m.resultado_operativo.toLocaleString('es-EC', {minimumFractionDigits:2})}`
                          : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══ TAB CHEQUES ══ */}
      {tab === 'cheques' && (
        <div style={{ padding: '24px 0' }}>
          {cargandoNuevo ? <p style={s.cargando}>Cargando cheques...</p> :
           !chequesMes ? <p style={{ color: '#e63946' }}>No se pudo cargar</p> : (
            <>
              {(chequesMes.meses || []).map((m, mi) => (
                <div key={m.mes} style={{ marginBottom: 24 }}>
                  {/* Header mes */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ background: '#1a2a3e', color: '#4cc9f0', borderRadius: 8, padding: '4px 16px', fontSize: 14, fontWeight: 'bold' }}>
                        {m.label}
                      </span>
                      <span style={{ color: '#555', fontSize: 12 }}>{m.num_cheques} cheques</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#555', fontSize: 10, fontWeight: 'bold', letterSpacing: 1 }}>TOTAL MES</div>
                      <div style={{ color: '#4caf50', fontWeight: 'bold', fontSize: 16 }}>
                        ${m.total.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>

                  {/* Tabla */}
                  <div style={s.tabla}>
                    <div style={s.tablaHeader}>
                      <span style={{ flex: 3 }}>PROVEEDOR</span>
                      <span style={{ width: 100, textAlign: 'center' }}>CHEQUE N°</span>
                      <span style={{ width: 120, textAlign: 'right' }}>VALOR</span>
                      <span style={{ width: 110, textAlign: 'center' }}>FECHA PAGO</span>
                    </div>
                    {m.cheques.map((ch, ci) => (
                      <div key={ci} style={{
                        ...s.tablaRow,
                        background: ci % 2 === 0 ? '#1e1e2e' : '#1a1a2e',
                      }}>
                        <span style={{ flex: 3 }}>
                          <div style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>{ch.proveedor}</div>
                          <div style={{ color: '#555', fontSize: 11 }}>{ch.ruc}</div>
                        </span>
                        <span style={{ width: 100, textAlign: 'center' }}>
                          <span style={{ background: '#1a2a3e', color: '#4cc9f0', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 'bold' }}>
                            #{ch.num_cheque}
                          </span>
                        </span>
                        <span style={{ width: 120, textAlign: 'right', color: '#4caf50', fontWeight: 'bold', fontSize: 13 }}>
                          ${ch.valor.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                        </span>
                        <span style={{ width: 110, textAlign: 'center', color: '#aaa', fontSize: 12 }}>
                          {ch.fecha}
                        </span>
                      </div>
                    ))}
                    {/* Total del mes */}
                    <div style={{ display: 'flex', padding: '10px 16px', background: '#0f0f1a', borderTop: '2px solid #2d2d44', gap: 8 }}>
                      <span style={{ flex: 3, color: '#aaa', fontSize: 12 }}>{m.num_cheques} cheques emitidos</span>
                      <span style={{ width: 100 }} />
                      <span style={{ width: 120, textAlign: 'right', color: '#4caf50', fontWeight: 'bold', fontSize: 14 }}>
                        ${m.total.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                      </span>
                      <span style={{ width: 110 }} />
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* ══ TAB CONDICIONES ══ */}
      {tab === 'condiciones' && (
        <div style={{ padding: '24px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button style={s.botonNuevo} onClick={() => abrirModalCond()}>+ Agregar proveedor</button>
          </div>
          {condiciones.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#555' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⚙️</div>
              <p>No hay condiciones configuradas.</p>
              <p style={{ fontSize: 12, marginTop: 8 }}>Agrega los proveedores con sus días de crédito para calcular vencimientos automáticamente.</p>
            </div>
          ) : (
            <div style={s.tabla}>
              <div style={s.tablaHeader}>
                <span style={{ flex: 3 }}>PROVEEDOR</span>
                <span style={{ flex: 2 }}>RUC</span>
                <span style={{ width: 220, textAlign: 'center' }}>CUOTAS (días desde fin de mes)</span>
                <span style={{ width: 120, textAlign: 'center' }}>AGRUPACIÓN</span>
                <span style={{ width: 110, textAlign: 'center' }}>ACTUALIZADO</span>
                <span style={{ width: 80,  textAlign: 'center' }}>EDITAR</span>
              </div>
              {condiciones.map((c, i) => (
                <div key={c.id} style={{ ...s.tablaRow, background: i%2===0?'#1e1e2e':'#1a1a2e' }}>
                  <span style={{ flex: 3, color: '#fff', fontSize: 13, fontWeight: '600' }}>{c.nombre_proveedor}</span>
                  <span style={{ flex: 2, color: '#555', fontSize: 12 }}>{c.ruc_proveedor}</span>
                  <span style={{ width: 220, textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {(c.dias_cuotas || '30,60,90,120').split(',').map((d, di) => (
                        <span key={di} style={{ background: '#1a2a3e', color: '#4cc9f0', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 'bold' }}>
                          {d.trim()}d
                        </span>
                      ))}
                    </div>
                  </span>
                  <span style={{ width: 120, textAlign: 'center', color: '#aaa', fontSize: 12 }}>
                    {c.tipo_agrupacion === 'mensual' ? '📅 Mensual' : '📄 Individual'}
                  </span>
                  <span style={{ width: 110, textAlign: 'center', color: '#555', fontSize: 11 }}>{c.actualizado}</span>
                  <span style={{ width: 80,  textAlign: 'center' }}>
                    <button style={s.botonVer} onClick={() => abrirModalCond(c)}>✏️ Editar</button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══ MODAL CONDICIÓN ══ */}
      {modalCond && (
        <div style={s.modalOverlay} onClick={() => setModalCond(null)}>
          <div style={{ ...s.modal, maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div style={{ color: '#fff', fontWeight: 'bold', fontSize: 15 }}>⚙️ Condición de Proveedor</div>
              <button style={s.modalCerrar} onClick={() => setModalCond(null)}>✕</button>
            </div>
            <div style={s.modalBody}>
              {[['RUC *','ruc_proveedor','Ej: 0990658498001'],['NOMBRE PROVEEDOR *','nombre_proveedor','Ej: INVERNEG S.A.']].map(([label, field, ph]) => (
                <div key={field} style={{ marginBottom: 16 }}>
                  <div style={{ color: '#555', fontSize: 11, fontWeight: 'bold', letterSpacing: 1, marginBottom: 6 }}>{label}</div>
                  <input style={s.inputModal} value={modalCond[field] || ''}
                    placeholder={ph}
                    onChange={e => setModalCond(p => ({ ...p, [field]: e.target.value }))} />
                </div>
              ))}
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: '#555', fontSize: 11, fontWeight: 'bold', letterSpacing: 1, marginBottom: 8 }}>DÍAS DE CUOTAS</div>
                <div style={{ color: '#aaa', fontSize: 11, marginBottom: 8 }}>
                  Ingresa los días separados por coma. Ej: <span style={{ color: '#4cc9f0' }}>30,60,90,120</span> o <span style={{ color: '#4cc9f0' }}>60,90,120,150</span>
                </div>
                <input style={s.inputModal}
                  value={modalCond.dias_cuotas || '30,60,90,120'}
                  placeholder="30,60,90,120"
                  onChange={e => setModalCond(p => ({ ...p, dias_cuotas: e.target.value }))}
                />
                {/* Preview de cuotas */}
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  {(modalCond.dias_cuotas || '30,60,90,120').split(',').filter(d => d.trim()).map((d, di) => (
                    <span key={di} style={{ background: '#1a2a3e', color: '#4cc9f0', borderRadius: 4, padding: '3px 10px', fontSize: 12, fontWeight: 'bold' }}>
                      Cuota {di+1}: {d.trim()}d
                    </span>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: '#555', fontSize: 11, fontWeight: 'bold', letterSpacing: 1, marginBottom: 8 }}>AGRUPACIÓN</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['mensual','📅 Mensual'],['individual','📄 Individual']].map(([v,l]) => (
                    <button key={v}
                      style={{
                        background: modalCond.tipo_agrupacion === v ? '#4cc9f0' : '#1e1e2e',
                        color:      modalCond.tipo_agrupacion === v ? '#0f0f1a' : '#aaa',
                        border: '1px solid #2d2d44', borderRadius: 6,
                        padding: '6px 14px', cursor: 'pointer', fontSize: 12
                      }}
                      onClick={() => setModalCond(p => ({ ...p, tipo_agrupacion: v }))}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: '#555', fontSize: 11, fontWeight: 'bold', letterSpacing: 1, marginBottom: 6 }}>NOTAS</div>
                <input style={s.inputModal} value={modalCond.notas || ''}
                  placeholder="Observaciones opcionales..."
                  onChange={e => setModalCond(p => ({ ...p, notas: e.target.value }))} />
              </div>
              <button
                style={{ ...s.botonNuevo, width: '100%', opacity: guardandoCond ? 0.6 : 1 }}
                onClick={guardarCond} disabled={guardandoCond}>
                {guardandoCond ? 'Guardando...' : '💾 Guardar condición'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ DRAWER CHEQUES ══ */}
      {drawerCheques && (
        <>
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 250 }}
            onClick={() => setDrawerCheques(null)} />
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, background: '#1a1a2e', borderLeft: '1px solid #2d2d44', zIndex: 260, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.5)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #2d2d44', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ color: '#fff', fontWeight: 'bold', fontSize: 15 }}>🧾 Cheques emitidos</div>
                  <div style={{ color: '#4cc9f0', fontSize: 13, marginTop: 4 }}>{drawerCheques.nombre}</div>
                  <div style={{ color: '#555', fontSize: 11, marginTop: 2 }}>{drawerCheques.ruc}</div>
                </div>
                <button style={{ background: 'none', border: '1px solid #444', borderRadius: 8, color: '#aaa', fontSize: 16, cursor: 'pointer', padding: '6px 12px' }}
                  onClick={() => setDrawerCheques(null)}>✕</button>
              </div>
              <div style={{ marginTop: 16, background: '#0f0f1a', borderRadius: 8, padding: '12px 16px', display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ color: '#555', fontSize: 10, fontWeight: 'bold', letterSpacing: 1 }}>TOTAL CHEQUES</div>
                  <div style={{ color: '#4caf50', fontSize: 20, fontWeight: 'bold', marginTop: 4 }}>
                    ${(drawerCheques.cheques || []).reduce((a, ch) => a + ch.valor, 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#555', fontSize: 10, fontWeight: 'bold', letterSpacing: 1 }}>CANTIDAD</div>
                  <div style={{ color: '#fff', fontSize: 20, fontWeight: 'bold', marginTop: 4 }}>
                    {(drawerCheques.cheques || []).length} cheque(s)
                  </div>
                </div>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
              {/* Agrupar cheques por mes, ordenado más reciente primero */}
              {(() => {
                const mesesUnicos = [...new Set((drawerCheques.cheques || []).map(ch => ch.mes))].sort((a, b) => b.localeCompare(a));
                return mesesUnicos.map((mes, i) => {
                  const chequesMes  = (drawerCheques.cheques || []).filter(ch => ch.mes === mes);
                  const recibsMes   = (drawerCheques.recibos || {})[mes] || [];
                  const recvalor    = recibsMes.reduce((a, r) => a + r.recvalor, 0);
                  const [a, m]      = mes.split('-');
                  const mesLabel    = `${MESES_ES[parseInt(m)]} ${a}`;
                  return (
                    <div key={mes} style={{ marginBottom: 16 }}>
                      {/* Header mes */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ background: '#1a2a3e', color: '#4cc9f0', borderRadius: 6, padding: '3px 12px', fontSize: 12, fontWeight: 'bold' }}>
                          {mesLabel}
                        </span>
                        {recvalor > 0 && (
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#555', fontSize: 9, fontWeight: 'bold', letterSpacing: 1 }}>VALOR MES DOCUMENTADO</div>
                            <div style={{ color: '#4caf50', fontWeight: 'bold', fontSize: 14 }}>
                              ${recvalor.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Tabla cheques individuales */}
                      <div style={{ background: '#0f0f1a', borderRadius: 8, overflow: 'hidden', border: '1px solid #2d2d44' }}>
                        <div style={{ display: 'flex', padding: '6px 12px', background: '#0a0a15', borderBottom: '1px solid #2d2d44' }}>
                          <span style={{ color: '#555', fontSize: 10, fontWeight: 'bold', letterSpacing: 1, width: 80 }}>CHEQUE N°</span>
                          <span style={{ color: '#555', fontSize: 10, fontWeight: 'bold', letterSpacing: 1, flex: 1, textAlign: 'right' }}>VALOR</span>
                          <span style={{ color: '#555', fontSize: 10, fontWeight: 'bold', letterSpacing: 1, width: 100, textAlign: 'right' }}>FECHA</span>
                        </div>
                        {chequesMes.map((ch, ci) => (
                          <div key={ci} style={{
                            display: 'flex', padding: '8px 12px', alignItems: 'center',
                            background: ci % 2 === 0 ? '#0f0f1a' : '#131320',
                            borderBottom: ci < chequesMes.length - 1 ? '1px solid #1a1a2e' : 'none',
                          }}>
                            <span style={{ color: '#4cc9f0', fontWeight: 'bold', fontSize: 12, width: 80 }}>#{ch.num_cheque}</span>
                            <span style={{ color: '#fff', fontWeight: 'bold', fontSize: 13, flex: 1, textAlign: 'right' }}>
                              ${ch.valor.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                            </span>
                            <span style={{ color: '#aaa', fontSize: 11, width: 100, textAlign: 'right' }}>{ch.fecha}</span>
                          </div>
                        ))}
                        {/* Subtotal del mes */}
                        <div style={{ display: 'flex', padding: '8px 12px', background: '#1a2a3e', borderTop: '1px solid #2d2d44' }}>
                          <span style={{ color: '#4cc9f0', fontSize: 11, fontWeight: 'bold', width: 80 }}>{chequesMes.length} cheques</span>
                          <span style={{ color: '#4cc9f0', fontWeight: 'bold', fontSize: 13, flex: 1, textAlign: 'right' }}>
                            ${chequesMes.reduce((a, ch) => a + ch.valor, 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                          </span>
                          <span style={{ width: 100 }} />
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
const s = {  
  inputBusqueda:   { width: '100%', background: '#0f0f1a', border: '1px solid #2d2d44', borderRadius: 8, color: '#fff', padding: '10px 14px', fontSize: 13, boxSizing: 'border-box' },
  cargando:        { color: '#aaa', padding: 40, textAlign: 'center' },
  headerRow:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  titulo:          { color: '#fff', marginBottom: 4 },
  subtitulo:       { color: '#555', fontSize: 13 },
  botonRefresh:    { background: 'none', border: '1px solid #444', borderRadius: 8, color: '#aaa', padding: '8px 16px', cursor: 'pointer', fontSize: 13 },
  tabs:            { display: 'flex', gap: 4, borderBottom: '1px solid #2d2d44', marginBottom: 0 },
  tab:             { background: 'none', border: 'none', borderBottom: '2px solid transparent', color: '#888', padding: '12px 20px', cursor: 'pointer', fontSize: 13 },
  tabActivo:       { color: '#fff', fontWeight: 'bold', borderBottomColor: '#4cc9f0' },
  kpisRow:         { display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' },
  kpiCard:         { background: '#1e1e2e', borderRadius: 12, padding: 20, flex: 1, minWidth: 140, borderTop: '3px solid #7209b7' },
  kpiValor:        { color: '#fff', fontSize: 28, fontWeight: 'bold', marginBottom: 4 },
  kpiLabel:        { color: '#aaa', fontSize: 12 },
  tabla:           { borderRadius: 8, overflow: 'hidden' },
  tablaHeader:     { display: 'flex', padding: '10px 16px', background: '#0f0f1a', color: '#555', fontSize: 11, fontWeight: 'bold', gap: 8 },
  tablaRow:        { display: 'flex', padding: '12px 16px', gap: 8, alignItems: 'center', transition: 'background 0.15s' },
  totalRow:        { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '16px 16px 0', borderTop: '1px solid #2d2d44', marginTop: 8 },
  proveedorHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, padding: '16px 20px', background: '#1e1e2e', borderRadius: 12 },
  botonVolver:     { background: 'none', border: '1px solid #444', borderRadius: 8, color: '#aaa', padding: '8px 16px', cursor: 'pointer', fontSize: 13 },
  botonVer:        { background: 'none', border: '1px solid #4cc9f0', borderRadius: 6, color: '#4cc9f0', padding: '4px 12px', cursor: 'pointer', fontSize: 12 },
  botonCheque:     { background: '#1a2a3e', border: '1px solid #4cc9f0', borderRadius: 6, color: '#4cc9f0', padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 'bold' },
  botonNuevo:      { background: '#1a2a3e', border: '1px solid #4cc9f0', borderRadius: 8, color: '#4cc9f0', padding: '8px 20px', cursor: 'pointer', fontWeight: 'bold', fontSize: 13 },
  filtroBtn:       { background: '#1e1e2e', border: '1px solid #2d2d44', borderRadius: 6, color: '#888', padding: '6px 14px', cursor: 'pointer', fontSize: 12 },
  filtroBtnActivo: { background: '#2d2d44', border: '1px solid #4cc9f0', color: '#4cc9f0', fontWeight: 'bold' },
  modalOverlay:    { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modal:           { background: '#1a1a2e', borderRadius: 16, border: '1px solid #2d2d44', width: '90%', maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  modalHeader:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 28px', borderBottom: '1px solid #2d2d44', flexShrink: 0 },
  modalCerrar:     { background: 'none', border: '1px solid #444', borderRadius: 8, color: '#aaa', fontSize: 16, cursor: 'pointer', padding: '6px 12px', marginLeft: 16 },
  modalBody:       { flex: 1, overflowY: 'auto', padding: 28 },
  inputModal:      { width: '100%', background: '#0f0f1a', border: '1px solid #2d2d44', borderRadius: 8, color: '#fff', padding: '10px 12px', fontSize: 13, boxSizing: 'border-box' },
};